"use client";

import { useMotionValue, useTransform } from "motion/react";
import * as m from "motion/react-m";
import clsx from "clsx";
import { useLayoutEffect } from "react";

import { getProgressRingRatios } from "./previewPlayerProgressRingMath";
import type { PreviewPlayerSize } from "./previewPlayerSizeConfig";
import { PREVIEW_PLAYER_SIZE_CONFIG } from "./previewPlayerSizeConfig";

function normalizeProgress(v: number) {
  return ((v % 1) + 1) % 1;
}

interface PreviewPlayerProgressRingProps {
  currentTime?: number;
  duration?: number;
  inverted?: boolean;
  isActive?: boolean;
  progressAnimationStartedAt?: number | null;
  size?: PreviewPlayerSize;
}

export function PreviewPlayerProgressRing({
  currentTime = 0,
  duration = 0,
  inverted = false,
  isActive = false,
  progressAnimationStartedAt = null,
  size = "md",
}: PreviewPlayerProgressRingProps) {
  const config = PREVIEW_PLAYER_SIZE_CONFIG[size];
  const tailRatio = useMotionValue(0);
  const tipRatio = useMotionValue(0.06);

  const dasharray = useTransform(() => {
    const dashLength = Math.max(tipRatio.get() - tailRatio.get(), 0);
    const dashGap = Math.max(1 - dashLength, 0.0005);
    return `${dashLength} ${dashGap}`;
  });

  const dashoffset = useTransform(() => {
    const tail = tailRatio.get();
    return tail === 0 ? 0 : -normalizeProgress(tail);
  });

  useLayoutEffect(() => {
    if (progressAnimationStartedAt === null) {
      tailRatio.set(0);
      tipRatio.set(0.06);
      return;
    }

    const ratios = getProgressRingRatios({ currentTime, duration });
    tailRatio.set(ratios.tailRatio);
    tipRatio.set(ratios.tipRatio);
  }, [currentTime, duration, progressAnimationStartedAt, tailRatio, tipRatio]);

  return (
    <div
      className={clsx(
        "tw-pointer-events-none tw-absolute tw-left-1/2 tw-top-1/2 -tw-translate-x-1/2 -tw-translate-y-1/2",
        config.ringWrapper,
        "tw-drop-shadow-[0_0.5px_0.5px_rgba(0,0,0,0.12)]",
        "tw-transition-transform tw-duration-500 tw-ease-[cubic-bezier(0.16,1,0.3,1)]",
        isActive ? "tw-scale-[0.925926]" : "tw-scale-[0.796296]",
        "group-hover/player:tw-scale-[0.925926]",
        "group-focus-visible/preview-button:tw-scale-[0.925926]",
      )}
    >
      <svg className='-tw-rotate-90' fill='none' viewBox={config.ringViewBox}>
        <m.circle
          cx={config.ringCenter}
          cy={config.ringCenter}
          r={config.ringRadius}
          stroke={inverted ? "black" : "white"}
          strokeLinecap='round'
          strokeWidth={config.ringStrokeWidth}
          pathLength={1}
          style={{
            strokeDasharray: dasharray,
            strokeDashoffset: dashoffset,
          }}
        />
      </svg>
    </div>
  );
}
